import React, { useState, useEffect } from 'react';
import { Search, Download, TrendingUp, Users, DollarSign } from 'lucide-react';
import axios from 'axios';

const BusinessReport = () => {
  const [reportData, setReportData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchBusinessReport();
  }, []);

  const fetchBusinessReport = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/consultants/reports/business`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReportData(response.data);
    } catch (error) {
      console.error('Error fetching business report:', error);
      alert('Failed to fetch business report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const filteredData = reportData.filter(item =>
    item.consultant_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalStats = {
    totalConsultants: reportData.length,
    totalStudents: reportData.reduce((sum, item) => sum + item.total_students, 0),
    totalBookings: reportData.reduce((sum, item) => sum + item.total_bookings, 0),
    totalCommission: reportData.reduce((sum, item) => sum + item.total_commission, 0)
  };

  const exportToCSV = () => {
    const headers = ['Consultant Name', 'Email', 'Phone', 'Total Students', 'Total Bookings', 'Conversions', 'Total Commission', 'Paid Amount', 'Balance Due'];
    const csvData = filteredData.map(item => [
      item.consultant_name,
      item.email,
      item.phone,
      item.total_students,
      item.total_bookings,
      item.converted_admissions,
      item.total_commission,
      item.total_paid || 0,
      item.balance_due || 0
    ]);
    
    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `business_report_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Business Report</h1>
          <p className="text-sm text-gray-500 mt-1">Consultant performance and business metrics</p>
        </div>
        <button
          onClick={exportToCSV}
          className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
        >
          <Download className="h-4 w-4" />
          Export CSV
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Consultants</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{totalStats.totalConsultants}</p>
            </div>
            <Users className="h-10 w-10 text-blue-500" />
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Students</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{totalStats.totalStudents}</p>
            </div>
            <TrendingUp className="h-10 w-10 text-green-500" />
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Bookings</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{totalStats.totalBookings}</p>
            </div>
            <TrendingUp className="h-10 w-10 text-purple-500" />
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Commission</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">₹{totalStats.totalCommission.toLocaleString('en-IN')}</p>
            </div>
            <DollarSign className="h-10 w-10 text-orange-500" />
          </div>
        </div>
      </div>

      {/* Search and Table */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b border-gray-200">
          <div className="relative">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search consultants..."
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Consultant</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Students</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bookings</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Conversions</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Commission</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Paid Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Balance Due</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="8" className="px-6 py-8 text-center text-gray-500">Loading...</td>
                </tr>
              ) : filteredData.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-8 text-center text-gray-500">No data available</td>
                </tr>
              ) : (
                filteredData.map((item) => (
                  <tr key={item.consultant_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{item.consultant_name}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-500">{item.email}</div>
                      <div className="text-sm text-gray-500">{item.phone}</div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">{item.total_students}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{item.total_bookings}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{item.converted_admissions}</td>
                    <td className="px-6 py-4 text-sm font-medium text-green-600">Rs. {item.total_commission.toLocaleString('en-IN')}</td>
                    <td className="px-6 py-4 text-sm font-medium text-blue-600">Rs. {(item.total_paid || 0).toLocaleString('en-IN')}</td>
                    <td className="px-6 py-4 text-sm font-medium text-orange-600">Rs. {(item.balance_due || 0).toLocaleString('en-IN')}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default BusinessReport;